import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, LogIn } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const LINKS = [
  { to: "/story", label: "Our Story" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/experiences", label: "Experiences" },
  { to: "/happenings", label: "Happenings" },
  { to: "/press", label: "Press" },
  { to: "/scholarships", label: "Scholarships" },
  { to: "/contact", label: "Contact" },
];

export default function PublicHeader() {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || !isHome;
  const logo = solid ? "/assets/logo-dark.png" : "/assets/logo-white.png";

  return (
    <header
      data-testid="public-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid ? "border-b border-[var(--pb-border)] bg-white/95 backdrop-blur" : "bg-gradient-to-b from-black/40 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-[1280px] items-center justify-between px-5 lg:px-8">
        <Link to="/" data-testid="header-logo" className="flex items-center">
          <img
            src={logo}
            alt="Pack Brothers Hospitality"
            className={`w-auto transition-all ${solid ? "h-11" : "h-12"}`}
          />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-[13px] font-medium uppercase tracking-[0.08em] transition-colors ${
                  solid
                    ? isActive
                      ? "text-[#18344A]"
                      : "text-[#526E7E] hover:text-[#18344A]"
                    : "text-white/85 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="header-team-login-cta"
            className={`hidden items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold uppercase tracking-[0.06em] transition-all md:inline-flex ${
              solid ? "bg-[#18344A] text-white hover:bg-[#18344A]/90" : "bg-white text-[#18344A] hover:bg-white/90"
            }`}
          >
            <LogIn className="h-4 w-4" /> Team Login
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="public-mobile-menu"
                aria-label="Open menu"
                className={`inline-flex h-10 w-10 items-center justify-center rounded-full lg:hidden ${
                  solid ? "text-[#18344A]" : "text-white"
                }`}
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-[#18344A] p-0 text-white">
              <div className="flex items-center justify-between px-6 py-5">
                <img src="/assets/logo-white.png" alt="Pack Brothers" className="h-10 w-auto" />
                <button onClick={() => setOpen(false)} aria-label="Close menu"><X className="h-5 w-5 text-white/80" /></button>
              </div>
              <nav className="flex flex-col px-4 pt-2">
                {LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-3 py-3 text-base font-medium text-white/85 hover:bg-white/10"
                  >
                    {l.label}
                  </NavLink>
                ))}
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-team-login-cta"
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#B7A58E] px-5 py-3 text-sm font-semibold uppercase tracking-wide text-[#18344A]"
                >
                  <LogIn className="h-4 w-4" /> Team Login
                </Link>
                <Link to="/portfolio" onClick={() => setOpen(false)} className="mt-3 px-3 py-2 text-center text-xs uppercase tracking-wide text-white/60 hover:text-white">
                  Explore Our Properties
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
